import dynamic from "next/dynamic";
import { useState, useEffect } from "react";
import { builderClient } from "../../../utils/builder";
import { BuilderComponent, Builder } from "@builder.io/react";
import Menu from "./Menu";
import { useRouter } from "next/compat/router";
import { useSelector } from "react-redux";
import { removeTargetAttr } from "@/helpers/generalHelper";

export const MenuBuilder = () => {
  const [builderContentJson, setBuilderContentJson] = useState(null);
  const menuData = useSelector((state) => state.general.menu_data);
  const [pwaBuilderData, setPwaBuilderData] = useState(null);
  const isPWA = useSelector((state) => state.pwa.is_PWA);
  const router = useRouter();

  const isImpactOrCharityPage =
  router.asPath.startsWith("/impact") ||
  router.asPath.startsWith("/charity");

  // Use useEffect hook to call API on component
  useEffect(() => {
    // to fix multiple api call issue
    if ((!menuData && !isPWA) || (isPWA && !pwaBuilderData)) {
      // Define user attributes object for API request
      const userAttributes = {
        urlPath: isPWA ? "/" : (isImpactOrCharityPage ? "/impact" : location.pathname),
        impactOrCharityPage: isPWA ? false : isImpactOrCharityPage
      };
      // Call API and get menu data
      builderClient
        .get("menu", { userAttributes })
        .promise()
        // Handle successful response  
        .then(data => {
          // Check PWA flag and set appropriate data
          if (isPWA) {
            setPwaBuilderData(data);
          } else {
            setBuilderContentJson(data);
          }
          // Remove target attribute
          removeTargetAttr();
        });
    }
   }, [isImpactOrCharityPage, isPWA, menuData, pwaBuilderData]);

  if (menuData && !isPWA && !pwaBuilderData)
    return <BuilderComponent model="menu" content={JSON.parse(menuData)} />;
  else if (isPWA && pwaBuilderData) {
    return <BuilderComponent model="menu" content={pwaBuilderData}/>
  }
  else{
    return <BuilderComponent model="menu" content={builderContentJson} />;
  }
};

const MenuComponent = dynamic(async () => {
  return (await import("./Menu")).default;
});

Builder.registerComponent(MenuComponent, {
  name: "Menu",
  inputs: [
    {
      name: "menuItems",
      type: "list",
      helperText: "Main Navigation Menu Items",
      defaultValue: [
        { navigationTitle: "New in" },
        { navigationTitle: "Designers" },
        { navigationTitle: "Clothing" },
        { navigationTitle: "Shoes" },
        { navigationTitle: "Bags & Accessories" },
        { navigationTitle: "Jewelry & watches" },
        { navigationTitle: "Beauty" },
        { navigationTitle: "Home" },
        { navigationTitle: "Inspiration" },
        { navigationTitle: "Sale" },
      ],
      subFields: [
        {
          name: "navigationTitle",
          type: "string",
          required: true,
          helperText: "Main Navigation Title",
        },
        {
          name: "navigationLink",
          type: "url",
          helperText: "Main Navigation Title URL",
        },
        {
          name: "subMenuColumns",
          type: "list",
          helperText: "You can only add a maximum of 5 columns",
          subFields: [
            {
              name: "squareTile",
              type: "boolean",
              defaultValue: false,
              helperText: "Select to display a square tile",
              showIf: `options.get('imageLink') == true`,
              onChange: `
                const currentImage = options.get("image"); // Store the current image
                if (options.get("squareTile")) {
                  // If switching to squareTile, save the current image as portraitImage
                  options.set("portraitImage", currentImage);
                  options.set("image", null); // Clear the image field
                } else {
                  // If switching to portraitTile, check if a new image is uploaded
                  const portraitImage = options.get("portraitImage");
                  if (!options.get("image") && portraitImage) {
                    // If no new image is uploaded, restore the old image
                    options.set("image", portraitImage);
                  }
                  options.set("portraitImage", null); // Clear the temporary portraitImage
                }
              `,
            },
            {
              name: "imageLink",
              type: "boolean",
              default: true,
              helperText:
                "Choose whether to display an image link in this column",
            },
            {
              name: "image",
              type: "file",
              allowedFileTypes: ["jpeg", "png", "gif"],
              helperText:
                "You can upload a file type that is either JPEG, PNG or GIF",
              showIf: `options.get('imageLink') == true`,
              required: true,
            },
            {
              name: "imageAltText",
              type: "string",
              helperText: "alt text",
            },
            {
              name: "header",
              type: "string",
              showIf: `options.get('imageLink') == true`,
              onChange: `if(options.get("header").length > 20) {options.set("header", options.get("header").slice(0, 20));alert("Maximum number of characters is 20")}`,
            },
            {
              name: "CTAText",
              type: "string",
              helperText: "Text that will be displayed for this CTA",
              showIf: `options.get('imageLink') == true`,
              onChange: `if(options.get("CTAText").length > 25) {options.set("CTAText", options.get("CTAText").slice(0, 25));alert("Maximum number of characters is 25")}`,
            },
            {
              name: "CTAUrl",
              type: "url",
              showIf: `options.get('imageLink') == true`,
              helperText: "CTAUrl & Storycard block url"
            },
            {
              name: "lightText",
              type: "boolean",
              defaultValue: false,
              helperText: "To change text color to light",
              showIf: `options.get('squareTile') == false && options.get('imageLink') == true`
            },
            {
              name: "hideOnMobile",
              type: "boolean",
              defaultValue: false,
              helperText: "Select to hide storycard on mobile & tablet",
            },
            {
              name: "columnTitle",
              type: "string",
              helperText: "Sub Navigation Column Title",
              showIf: `!options.get('imageLink') || options.get('imageLink') == false`,
            },
            {
              name: "columnItems",
              type: "list",
              helperText: "Each column should have a maximum of 20 items.",
              showIf: `!options.get('imageLink') || options.get('imageLink') == false`,
              subFields: [
                {
                  name: "subTitle",
                  type: "string",
                  helperText: "Column item subtitle",
                },
                {
                  name: "subTitleLink",
                  type: "url",
                  helperText: "Column item subtitle URL",
                },
              ],
            },
          ],
        },
      ],
      onChange: (options) => {
        if (options.get("menuItems").length == 0) {
          alert("You need to add at least one menu item");
        }
      },
    },
  ],
});
